type TypedValueKind = 'date' | 'time' | 'dateTime' | 'boolean';

const TYPED_VALUE_FORMATS: Record<TypedValueKind, readonly string[]> = {
  date: ['@septim/date:short', '@septim/date:long'],
  time: ['@septim/time:short', '@septim/time:seconds'],
  dateTime: ['@septim/dateTime:short', '@septim/dateTime:seconds'],
  boolean: ['@septim/boolean:yesNo', '@septim/boolean:trueFalse'],
};

const isTypedValueKind = (type?: string): type is TypedValueKind =>
  type === 'date' ||
  type === 'time' ||
  type === 'dateTime' ||
  type === 'boolean';

const isTypedValueFormat = (type: TypedValueKind, format?: string): boolean =>
  !format || TYPED_VALUE_FORMATS[type].includes(format);

const normalizeLocale = (locale?: string): 'cs-CZ' | 'en-US' =>
  locale?.toLowerCase().startsWith('en') ? 'en-US' : 'cs-CZ';

const parseTime = (value: unknown): Date | null => {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(value));
  if (!match) {
    return null;
  }
  const date = new Date(
    2000,
    0,
    1,
    Number(match[1]),
    Number(match[2]),
    Number(match[3] ?? 0)
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseDate = (value: unknown): Date | null => {
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Applies the stable report-only typed format contract shared with the designer. */
export const formatTypedPrintValue = (
  value: unknown,
  type?: string,
  format?: string,
  locale?: string
): string | null => {
  if (!isTypedValueKind(type) || !isTypedValueFormat(type, format)) {
    return null;
  }

  const resolvedLocale = normalizeLocale(locale);
  if (type === 'boolean') {
    if (typeof value !== 'boolean') {
      return String(value);
    }
    if (format === '@septim/boolean:trueFalse') {
      if (resolvedLocale === 'cs-CZ') {
        return value ? 'Pravda' : 'Nepravda';
      }
      return value ? 'True' : 'False';
    }
    if (resolvedLocale === 'cs-CZ') {
      return value ? 'Ano' : 'Ne';
    }
    return value ? 'Yes' : 'No';
  }

  const date = type === 'time' ? parseTime(value) : parseDate(value);
  if (!date) {
    return String(value);
  }

  if (type === 'date') {
    return new Intl.DateTimeFormat(resolvedLocale, {
      day: '2-digit',
      month: format === '@septim/date:long' ? 'long' : '2-digit',
      year: 'numeric',
    }).format(date);
  }

  const includeSeconds =
    format === '@septim/time:seconds' || format === '@septim/dateTime:seconds';
  if (type === 'time') {
    return new Intl.DateTimeFormat(resolvedLocale, {
      hour: '2-digit',
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
    }).format(date);
  }

  return new Intl.DateTimeFormat(resolvedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
  }).format(date);
};
